import { createSlice } from '@reduxjs/toolkit';

import { createPatientHandoff, fetchHandoffEnumsConfig, fetchPatientHandoffByPatientId, updatePatientHandoff } from './handoff-api';
import type { HandoffState } from './types';
import { logout } from '../auth/logout';

export {
    createPatientHandoff,
    deletePatientHandoff,
    fetchHandoffEnumsConfig,
    fetchPatientHandoffById,
    fetchPatientHandoffByPatientId,
    updatePatientHandoff
} from './handoff-api';
export type {
    DeletePatientHandoffArg,
    FetchPatientHandoffByIdArg,
    FetchPatientHandoffByPatientIdArg,
    UpdatePatientHandoffArg
} from './handoff-api';
export type { HandoffState } from './types';

const initialState: HandoffState = {
    enumsConfig: null,
    enumsCacheKey: null,
    enumsLoading: false,
    enumsError: null,
    submitLoading: false,
    submitError: null,
    patientHandoff: null,
    patientHandoffPatientId: null,
    patientHandoffDetailLoading: false,
    patientHandoffDetailError: null,
    patientHandoffUpdateLoading: false,
    patientHandoffUpdateError: null
};

const handoffSlice = createSlice({
    name: 'handoff',
    initialState,
    reducers: {
        clearHandoffEnums: (state) => {
            state.enumsConfig = null;
            state.enumsCacheKey = null;
            state.enumsError = null;
            state.submitLoading = false;
            state.submitError = null;
            state.patientHandoffDetailLoading = false;
            state.patientHandoffDetailError = null;
            state.patientHandoffUpdateLoading = false;
            state.patientHandoffUpdateError = null;
        },
        clearPatientHandoff: (state) => {
            state.patientHandoff = null;
            state.patientHandoffPatientId = null;
            state.patientHandoffDetailLoading = false;
            state.patientHandoffDetailError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHandoffEnumsConfig.pending, (state) => {
                state.enumsLoading = true;
                state.enumsError = null;
            })
            .addCase(fetchHandoffEnumsConfig.fulfilled, (state, action) => {
                state.enumsLoading = false;
                state.enumsConfig = action.payload;
                state.enumsCacheKey = action.meta.arg?.hospitalId ?? 'default';
                state.enumsError = null;
            })
            .addCase(fetchHandoffEnumsConfig.rejected, (state, { payload, error }) => {
                state.enumsLoading = false;
                if (error.name === 'AbortError') {
                    state.enumsError = null;
                    return;
                }
                state.enumsError = payload ?? 'Failed to load handoff options';
            })
            .addCase(createPatientHandoff.pending, (state) => {
                state.submitLoading = true;
                state.submitError = null;
            })
            .addCase(createPatientHandoff.fulfilled, (state) => {
                state.submitLoading = false;
                state.submitError = null;
            })
            .addCase(createPatientHandoff.rejected, (state, { payload }) => {
                state.submitLoading = false;
                state.submitError = payload ?? 'Failed to submit handoff';
            })
            .addCase(fetchPatientHandoffByPatientId.pending, (state) => {
                state.patientHandoffDetailLoading = true;
                state.patientHandoffDetailError = null;
            })
            .addCase(fetchPatientHandoffByPatientId.fulfilled, (state, action) => {
                state.patientHandoffDetailLoading = false;
                state.patientHandoffDetailError = null;
                state.patientHandoff = action.payload;
                state.patientHandoffPatientId = action.meta.arg.patientId;
            })
            .addCase(fetchPatientHandoffByPatientId.rejected, (state, { payload, error }) => {
                state.patientHandoffDetailLoading = false;
                if (error.name === 'AbortError') {
                    state.patientHandoffDetailError = null;
                    return;
                }
                state.patientHandoffDetailError = payload ?? 'Failed to load patient handoff';
            })
            .addCase(updatePatientHandoff.pending, (state) => {
                state.patientHandoffUpdateLoading = true;
                state.patientHandoffUpdateError = null;
            })
            .addCase(updatePatientHandoff.fulfilled, (state) => {
                state.patientHandoffUpdateLoading = false;
                state.patientHandoffUpdateError = null;
            })
            .addCase(updatePatientHandoff.rejected, (state, { payload, error }) => {
                state.patientHandoffUpdateLoading = false;
                if (error.name === 'AbortError') {
                    state.patientHandoffUpdateError = null;
                    return;
                }
                state.patientHandoffUpdateError = payload ?? 'Failed to update patient handoff';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearHandoffEnums, clearPatientHandoff } = handoffSlice.actions;
export default handoffSlice.reducer;
